import { useCallback, useSyncExternalStore } from 'react';

/** ``chart`` = chart tiles + OI blue=high (default). ``classic`` = dial tiles + green=high. */
export type DashboardTileStyle = 'chart' | 'classic';

const STORAGE_KEY = 'dashboard-tile-style';
const CHANGE_EVENT = 'dashboard-tile-style-change';

// Fallback for when storage is unavailable (private mode etc.), so the toggle still works this session.
let memoryStyle: DashboardTileStyle = 'chart';

const readStyle = (): DashboardTileStyle => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'classic' || stored === 'chart') return stored;
  } catch {
    // Fall through to the in-memory value.
  }
  return memoryStyle;
};

const subscribe = (onChange: () => void) => {
  // ``storage`` syncs other tabs; the custom event syncs every mounted consumer in this tab.
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
};

/**
 * The viewer's preferred dashboard tile layout, persisted per browser.
 * Every component reading it re-renders together when it changes.
 */
export const useDashboardTileStyle = () => {
  const tileStyle = useSyncExternalStore(subscribe, readStyle, () => 'chart' as const);

  const setTileStyle = useCallback((next: DashboardTileStyle) => {
    memoryStyle = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable — the in-memory value carries the change until reload.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { tileStyle, setTileStyle };
};

/** Shorthand for components that only need to know which tile layout to render. */
export const useChartTilesEnabled = () => useDashboardTileStyle().tileStyle === 'chart';
